import * as React from 'react'

import { ObjectHash } from '../components/types'

type IntersectionObservedEntry = ObjectHash<(isIntersecting: boolean) => void>

/*
 * Context.
 */

export interface IntersectionObserverContextProps {
  intersectionObservedElementsRef: React.MutableRefObject<IntersectionObservedEntry>
  registerIntersectionObservedElement: (
    elementId: string,
    callback: (isObserving: boolean) => void
  ) => (() => void) | null
}

export const IntersectionObserverContext = React.createContext<IntersectionObserverContextProps>({
  intersectionObservedElementsRef: { current: {} },
  registerIntersectionObservedElement: () => null,
})

/*
 * Hook.
 */

export function useIntersectionObserverContext(): IntersectionObserverContextProps {
  return React.useContext(IntersectionObserverContext)
}

/*
 * Component.
 */

interface IntersectionObserverContextProviderProps {
  children: React.ReactNode
}
export const IntersectionObserverContextProvider = ({ children }: IntersectionObserverContextProviderProps) => {
  const intersectionObservedElementsRef = React.useRef<IntersectionObservedEntry>({})
  const intersectionObserverRef = React.useRef(
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const callback = intersectionObservedElementsRef.current[entry.target.id]
        callback?.(entry.isIntersecting)
      })
    })
  )

  React.useEffect(
    () => () => {
      intersectionObserverRef.current.disconnect()
    },
    []
  )

  const registerIntersectionObservedElement = React.useCallback(
    (elementId: string, callback: (isObserving: boolean) => void) => {
      const element = document.getElementById(elementId)
      if (!element) {
        return null
      }
      intersectionObserverRef.current.observe(element)
      intersectionObservedElementsRef.current[elementId] = callback

      return () => {
        intersectionObserverRef.current.unobserve(element)
        delete intersectionObservedElementsRef.current[elementId]
      }
    },
    []
  )

  return (
    <IntersectionObserverContext.Provider
      value={{ intersectionObservedElementsRef, registerIntersectionObservedElement }}
    >
      {children}
    </IntersectionObserverContext.Provider>
  )
}
